import { reactive, ref, Ref, computed, ComputedRef } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import smh from '@/renderer/scripts/smh';
import user from '@/renderer/scripts/utils/user';
import store from '@/renderer/store/index';
import ipc from '@/renderer/scripts/utils/ipc';
import getRequest from '@/renderer/scripts/httpRequest';
import { getImgWidthHeight } from '@/renderer/scripts/utils/tools/index';
import { httpUrl, CONSTANT_URL } from '@/renderer/scripts/http';

type UserInfo = {
  nickname: string;
  email: string;
  phoneNumber: string;
  comment: string;
  avatar: string;
  role: string;
  hasPersonalSpace: boolean;
  capacity: string; // 已分配的空间大小
  availableSpace: string; // 可用空间大小
};
export enum ShowWitchEditType {
  none = 'none',
  nickname = 'nickname',
  phone = 'phone',
  email = 'email',
  password = 'password',
  space = 'space',
}
type Handler = {
  sendSMS: (countryCode: string, phoneNumber: string) => Promise<boolean>;
  updatePhoneNumber: (countryCode: string, phoneNumber: string, code: string) => Promise<boolean>;
  userInfo: UserInfo;
  uploading: Ref<boolean>;
  setBaseInfo: (info: {}) => Promise<void>;
  upload: (file: File) => Promise<void>;
  showSwitchOrgEntry: ComputedRef<boolean>;
};
export default function pullListHandler(): Handler {
  const cosRequest = getRequest({
    logPrefix: 'UploadAvatar',
  });
  const { nickname, email, phoneNumber, avatar, role, hasPersonalSpace, capacity, availableSpace } =
    store.getters.userBaseInfo;
  const userInfo = reactive<UserInfo>({
    nickname,
    email,
    phoneNumber,
    comment: '',
    avatar,
    role,
    hasPersonalSpace,
    // 已分配的空间大小
    capacity,
    // 可用空间大小
    availableSpace,
  });

  const uploading = ref(false);

  /**
   * 是否展示切换企业入口
   * 自定义域名不展示；多企业身份；会议里未领取体验版就可以展示；
   * */
  const showSwitchOrgEntry = computed(
    () =>
      !CONSTANT_URL.isCustomizedDomain &&
      (store.getters.multiOrg || (!store.getters.hasAppliedChannelOrg && store.getters.isInWeMeetApp)),
  );
  const methods = {
    // 设置当前用户基本信息
    async setBaseInfo(info: {}) {
      const {
        userId,
      }: {
        userId: number;
      } = store.getters.userBaseInfo;
      try {
        await smh.userAPI.updateUser({
          userId,
          ...info,
        });
        store.commit('SET_BASEINFO', info);
        ipc.emit(ipc.RENDERER_EVENTS_MAP.USER_INFO_CHANGE);
        tips.success(i18n_t('修改成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 上传头像
    async upload(file: File) {
      const { width, height } = await getImgWidthHeight(file);
      if (width < 256 || height < 256) {
        tips.error(i18n_t('图片像素至少为256*256'));
        return;
      }
      try {
        tips.loading(i18n_t('正在上传'));
        uploading.value = true;
        const fileType = file.name.slice(file.name.lastIndexOf('.') + 1);
        const res = await smh.userAPI.putAvatar({
          ext: fileType,
        });
        const { domain, headers, path } = res.data;
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = async (e: Event) => {
          const body = (e.target as any).result;
          const resPath = path.startsWith('/') ? path.slice(1) : path;
          // 上传到cos
          try {
            await cosRequest({
              url: `${httpUrl.protocol}://${domain}/${encodeURI(resPath)}`,
              method: 'PUT',
              headers,
              data: body,
            });
          } catch (e) {
            uploading.value = false;
            tips.error(e as MsgError);
            return;
          }
          // 确认上传头像
          try {
            const avatarRes = await smh.userAPI.postAvatar({
              filePath: resPath,
            });
            const { avatar } = avatarRes.data;
            store.commit('SET_BASEINFO', {
              avatar: avatar ? `${httpUrl.protocol}:${avatar}` : '',
            });
            tips.success(i18n_t('上传成功'));
          } catch (e) {
            tips.error(e as MsgError);
          }
          uploading.value = false;
        };
      } catch (e) {
        uploading.value = false;
        tips.error(e as MsgError);
      }
    },
    /**
     * @description 修改手机号-发送验证码
     * @param countryCode 地区码
     * @param phoneNumber 手机号
     * @returns 是否取消倒计时
     */
    async sendSMS(countryCode: string, phoneNumber: string): Promise<boolean> {
      try {
        await smh.userAPI.updatePhoneSend({
          countryCode,
          phoneNumber,
        });
        tips.success(i18n_t('验证码发送成功'));
        return false;
      } catch (e: any) {
        tips.error(e as MsgError);
        const { code } = e;
        if (code === 'SmsFrequencyLimit') {
          return false;
        }
        return !!code;
      }
    },
    // 修改手机号-确认修改
    async updatePhoneNumber(countryCode = '+86', phoneNumber: string, code: string): Promise<boolean> {
      try {
        if (!code || code.trim().length === 0) {
          tips.error(i18n_t('验证码不可为空'));
          return false;
        }
        if (!code || !/\d{5,6}/.test(code)) {
          tips.error(i18n_t('请输入正确格式的验证码'));
          return false;
        }
        const innerPhonePattern = /^1[3-9]\d{9}$/;
        const overseasPhonePattern = /^\d{3,20}$/;
        const checker = countryCode === '+86' ? innerPhonePattern : overseasPhonePattern;
        if (!(phoneNumber && checker.test(phoneNumber))) {
          tips.error(i18n_t('请输入正确格式的手机号码'));
          return false;
        }
        await smh.userAPI.updatePhoneConfirm({
          countryCode,
          phoneNumber,
          code,
        });
        tips.success(i18n_t('修改成功, 请重新登录'));
        store.commit('SET_BASEINFO', {
          phoneNumber,
        });
        // 手机号码修改完需要重新登录
        setTimeout(() => {
          user.signOut();
        }, 1000);
        return true;
      } catch (e) {
        tips.error(e as MsgError);
        return false;
      }
    },
  };
  return {
    userInfo,
    uploading,
    showSwitchOrgEntry,
    ...methods,
  };
}
